import React, { useState } from 'react';
import { Game } from '../types';
import './GameRow.css';

interface GameRowProps {
  game: Game;
  index: number;
  onLevelChange: (gameId: string, newLevel: number) => void;
  onDescriptionChange: (gameId: string, description: string) => void;
  onNotesChange: (gameId: string, notes: string) => void;
}

const GameRow: React.FC<GameRowProps> = ({
  game,
  index,
  onLevelChange,
  onDescriptionChange,
  onNotesChange
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');

  const handleStartEdit = (field: string, currentValue: string | number = '') => {
    setEditingField(field);
    setTempValue(String(currentValue));
  };

  const handleSaveEdit = () => {
    if (!editingField) return;

    switch (editingField) {
      case 'level':
        const newLevel = parseInt(tempValue);
        if (!isNaN(newLevel) && newLevel >= 0) {
          onLevelChange(game.id, newLevel);
        }
        break;
      case 'description':
        onDescriptionChange(game.id, tempValue);
        break;
      case 'notes':
        onNotesChange(game.id, tempValue);
        break;
    }

    setEditingField(null);
    setTempValue('');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const calculateProgress = () => {
    if (!game.maxLevel) return 0;
    return Math.min((game.currentLevel / game.maxLevel) * 100, 100);
  };

  const getProgressColor = (progress: number) => {
    if (progress < 25) return '#ff6b6b';
    if (progress < 50) return '#ffa726';
    if (progress < 75) return '#66bb6a';
    return '#4caf50';
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <tr className="game-row">
      <td className="col-index">{index}</td>
      
      <td className="col-name">
        <div className="game-name">
          {game.name}
        </div>
      </td>
      
      <td className="col-level">
        {editingField === 'level' ? (
          <div className="edit-field">
            <input
              type="number"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="level-input"
              min="0"
              autoFocus
            />
          </div>
        ) : (
          <div 
            className="level-cell clickable"
            onClick={() => handleStartEdit('level', game.currentLevel)}
          >
            <span className="level-number">{game.currentLevel}</span>
            <span className="edit-icon">✏️</span>
          </div>
        )}
      </td>
      
      <td className="col-max-level">
        {game.maxLevel || '-'}
      </td>
      
      <td className="col-progress">
        {game.maxLevel ? (
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  width: `${calculateProgress()}%`,
                  backgroundColor: getProgressColor(calculateProgress())
                }}
              ></div>
            </div>
            <span className="progress-text">
              {calculateProgress().toFixed(1)}%
            </span>
          </div>
        ) : (
          <span className="no-progress">-</span>
        )}
      </td>
      
      <td className="col-description">
        {editingField === 'description' ? (
          <div className="edit-field">
            <input
              type="text"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="description-input"
              autoFocus
            />
          </div>
        ) : (
          <div 
            className="description-cell clickable"
            onClick={() => handleStartEdit('description', game.description)}
          >
            <span className="description-text">
              {game.description || 'Açıklama ekle...'}
            </span>
            <span className="edit-icon">✏️</span>
          </div>
        )}
      </td>
      
      <td className="col-notes">
        {editingField === 'notes' ? (
          <div className="edit-field">
            <textarea
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="notes-textarea"
              autoFocus
              rows={2}
            />
          </div>
        ) : (
          <div 
            className="notes-cell clickable"
            onClick={() => handleStartEdit('notes', game.notes || '')}
          >
            <span className="notes-text">
              {game.notes || 'Not ekle...'}
            </span>
            <span className="edit-icon">✏️</span>
          </div>
        )}
      </td>
      
      <td className="col-last-played">
        {formatDate(game.lastPlayed)}
      </td>
    </tr>
  );
};

export default GameRow;
